import Price from './format-price';

/**
 * Calculation class used for all price calculations (lane, room high, sheeting)
 * @param {object} product - product data from the product page
 * @param {object} matrix - additional data from the metaobject material_factor, manufacture_costs, surcharges and sheetings
 * @param {object} surcharges - additional data from the metaobject surcharges
 * @param {object} inputData - form data from the product page or line item properties
 * @returns {object} price calculation
 */
class PriceCalculation {
  constructor(productData, matrix, surcharges, inputData) {
    this.product = productData;
    this.matrix = matrix;
    this.surcharges = surcharges;
    this.inputData = inputData;

    this.options = {
      debug: false,
    };

    if (this.options.debug && !this.product) {
      console.log('No product data');
      return;
    }

    if (this.options.debug && !this.matrixData) {
      console.log('No additional data');
      return;
    }

    if (this.options.debug && !this.inputData) {
      console.log('No form data');
      return;
    }

    if (this.options.debug) console.log('Calculator', this);

    try {
      // Get often used product data
      this.productType = handleize(this.product.product_type);

      // Get the input data
      this.quantity = this.inputData.quantity ? parseInt(this.inputData.quantity) : 1;
      this.inputWidth = this.inputData.width ? parseFloat(this.inputData.width) : 0;
      this.inputHeight = this.inputData.height ? parseFloat(this.inputData.height) : 0;

      // Get the additional data
      this.surcharges = this.getSurcharges();

      // Initialize the price class
      this.price = new Price(
        this.product.variant_id,
        this.product.from_price,
        this.product.from_compare_at_price,
        this.quantity
      );

      // Validate the matrix data
      const validateMatrix = this.validateMatrix();
      if (validateMatrix.errors.length >= 1) return validateMatrix;

      // Validate the width and height input
      const validateWidthAndHeight = this.validateWidthAndHeight();
      if (validateWidthAndHeight.errors.length >= 1) return validateWidthAndHeight;

      // Step 1: Get the matrix costs
      const matrixCosts = this.getMatrixCosts();
      if (matrixCosts?.errors && matrixCosts.errors.length >= 1) return matrixCosts;

      // Step 2 - Calculate the surcharge costs
      const surchargeCosts = this.surchargeCosts(matrixCosts);
      if (surchargeCosts?.errors && surchargeCosts.errors.length >= 1) return surchargeCosts;

      // Step 3: Return the formatted price
      const totalPrice = this.totalPrice(matrixCosts, surchargeCosts);

      // Use to debug the calculation
      if (this.options.debug) {
        console.log('Price calculation', {
          'matrix costs': matrixCosts,
          'total price': totalPrice,
        });
      }

      // Return the formatted price
      return totalPrice;
    } catch (error) {
      console.error(error);
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price, true),
        errors: [
          {
            type: 'general',
            handle: 'something_went_wrong',
            message: `Sorry, something went wrong. Please try again.`,
          },
        ],
      };
    }
  }

  /**
   * Get the surcharge based of curtain type, fabric type and pleating
   * @param {string} curtainType
   * @param {string} fabricType
   * @param {string} pleating
   * @returns {array} with surcharges objects
   */
  getSurcharges(productType = this.productType) {
    return this.surcharges.filter((surcharge) => {
      if (!surcharge.data['product_type']) return false;
      return surcharge.data['product_type'].find((type) => handleize(type) == productType);
    });
  }

  /**
   * Validate if the matrix data is available with at least one valid row
   * @returns {object} formatted price with eventual errors
   */
  validateMatrix() {
    // Initialize the errors array
    const errors = [];

    // Check if the matrix data is available else return an error
    if (
      !this.matrix ||
      !this.matrix.price ||
      !this.matrix.price[0].width ||
      !this.matrix.price[0].height ||
      !this.matrix.price[0].price
    ) {
      errors.push({
        type: 'general',
        handle: 'invalid_matrix',
        message: "Sorry, we couldn't find valid price. Please contact us for a quote.",
      });
    }

    // Check if the compare at price is available and filled in correctly
    if (
      this.matrix.compare_at_price &&
      (!this.matrix.compare_at_price[0].width ||
        !this.matrix.compare_at_price[0].height ||
        !this.matrix.compare_at_price[0].price)
    ) {
      errors.push({
        type: 'general',
        handle: 'invalid_compare_at_price',
        message:
          'Sorry, we could not find a valid compare at price. Please contact us for a quote.',
      });
    }

    // Return the formatted price with the errors
    return {
      ...this.price.format(this.product.from_price, this.product.from_compare_at_price, true),
      errors,
    };
  }

  /**
   * Validate width and height input
   * @returns {object} formatted price with eventual errors
   */
  validateWidthAndHeight() {
    // Initialize the errors array
    const errors = [];

    // Check if the width is valid valid and set the minimum width for further calculations
    if (!this.inputWidth || this.inputWidth < this.product['min_width']) {
      this.inputWidth = this.product['min_width'];
      errors.push({
        type: 'width',
        handle: 'invalid_width',
        message: 'Please enter a valid width.',
      });
    }

    // Check if the height is valid valid and set the minimum height for further calculations
    if (!this.inputHeight || this.inputHeight < this.product['min_height']) {
      this.inputHeight = this.product['min_height'];
      errors.push({
        type: 'height',
        handle: 'invalid_height',
        message: 'Please enter a valid height.',
      });
    }

    // Return the formatted price with the errors
    return {
      ...this.price.format(this.product.from_price, this.product.from_compare_at_price, true),
      errors,
    };
  }

  /**
   * Step 1 - Get the matrix costs base on the input width and height
   * @returns {object} fabric costs with @price and @compare_at_price
   */
  getMatrixCosts() {
    // Find the first row that is greater or equal to the input width and height
    const matrixRow = this.matrix.price.find(
      (row) => row.width >= this.inputWidth && row.height >= this.inputHeight
    );

    // Return an error if no matching row was found
    if (!matrixRow) {
      return {
        ...this.price.format(this.product.from_price, this.product.from_compare_at_price, true),
        errors: [
          {
            type: 'general',
            handle: 'invalid_dimensions',
            message: 'Sorry, we could not find a matching price for the given dimensions.',
          },
        ],
      };
    }

    // Get the compare at price if available
    if (!this.matrix.compare_at_price) {
      return {
        price: matrixRow.price,
        compare_at_price: matrixRow.price,
      };
    }

    // Get the compare at price if available
    const compareAtMatrixRow = this.matrix.compare_at_price.find(
      (row) => row.width >= this.inputWidth && row.height >= this.inputHeight
    );

    // Return the price from the matrix row
    return {
      price: matrixRow.price,
      compare_at_price: compareAtMatrixRow ? compareAtMatrixRow.price : matrixRow.price,
    };
  }

  /**
   * Step 2 - Calculate the surcharge costs
   * @param {object} matrixCosts - price from the matrix row
   * @returns {number} surcharge costs in cents
   */
  surchargeCosts(matrixCosts) {
    return this.surcharges.reduce(
      (costs, surcharge) => {
        let updated = false;

        // Check if the surcharge is chain specific and return if the chain is not the same
        if (surcharge.data['sheeting_type']) {
          const selectedSheeting = this.inputData['sheeting_type']
            ? handleize(this.inputData['sheeting_type'])
            : '';
          const surchargeSheetings = surcharge.data['sheeting_type'].map((type) => handleize(type));
          if (!surchargeSheetings.includes(selectedSheeting)) {
            return costs;
          }
        }

        // Check if the surcharge is chain specific and return if the chain is not the same
        if (surcharge.data['chain']) {
          const selectedChain = this.inputData['chain'] ? handleize(this.inputData['chain']) : '';
          const surchargeChains = surcharge.data['chain'].map((chain) => handleize(chain));
          if (!surchargeChains.includes(selectedChain)) {
            return costs;
          }
        }

        // Check if the surcharge is blind material specific
        if (surcharge.data['blinds_material']) {
          const selectedBlindMaterial = this.inputData['material']
            ? handleize(this.inputData['material'])
            : '';
          const surchargeBlindMaterials = surcharge.data['blinds_material'].map((material) =>
            handleize(material)
          );
          if (!surchargeBlindMaterials.includes(selectedBlindMaterial)) {
            return costs;
          }
        }

        // Check if the surcharge is blinds tape specific
        if (surcharge.data['blinds_tape']) {
          const selectedBlindTape =
            this.inputData['tape'] && this.inputData['tape'] == 'yes' ? true : false;
          const surchargeBlindTape = surcharge.data['blinds_tape'] ? true : false;
          if (selectedBlindTape != surchargeBlindTape) {
            return costs;
          }
        }

        // Check if the surcharge is Window type specific
        if (surcharge.data['window_type']) {
          const selectedWindowType = this.inputData['window_type']
            ? handleize(this.inputData['window_type'])
            : '';
          const surchargeWindowTypes = surcharge.data['window_type'].map((type) => handleize(type));
          if (!surchargeWindowTypes.includes(selectedWindowType)) {
            return costs;
          }
        }

        // Check if the surcharge is control direction specific
        if (surcharge.data['control_direction']) {
          const selectedControlDirection = this.inputData['control_direction']
            ? handleize(this.inputData['control_direction'])
            : '';
          const surchargeControlDirections = surcharge.data['control_direction'].map((direction) =>
            handleize(direction)
          );
          if (!surchargeControlDirections.includes(selectedControlDirection)) {
            return costs;
          }
        }

        // Check if the surcharge is type of attachment specific
        if (surcharge.data['type_of_attachment']) {
          const selectedAttachment = this.inputData['type_of_attachment']
            ? handleize(this.inputData['type_of_attachment'])
            : '';
          const surchargeAttachments = surcharge.data['type_of_attachment'].map((attachment) =>
            handleize(attachment)
          );
          if (!surchargeAttachments.includes(selectedAttachment)) {
            return costs;
          }
        }

        // Check if the surcharge is draw rod specific
        if (surcharge.data['draw_rod']) {
          const selectedDrawRod =
            this.inputData['draw_rod'] && this.inputData['draw_rod'] == 'yes' ? true : false;
          const surchargeDrawRod = surcharge.data['draw_rod'] ? true : false;
          if (selectedDrawRod != surchargeDrawRod) {
            return costs;
          }
        }

        // Apply the percentage surcharge
        if (!updated && surcharge.data['percentage']) {
          costs.price =
            costs.price +
            Math.round((matrixCosts.price / 100) * parseInt(surcharge.data['percentage']));
          costs.compare_at_price = Math.round(
            costs.compare_at_price +
              (matrixCosts.compare_at_price / 100) * parseInt(surcharge.data['percentage'])
          );
          updated = true;
        }

        // Apply the fixed surcharge
        if (!updated && surcharge.data['price_per_piece']) {
          const pieces = surcharge.data['pieces_per_partition']
            ? surcharge.data['pieces_per_partition']
            : 1;
          const piecesPrice = parseInt(surcharge.data['price_per_piece']) * pieces;
          costs.price = costs.price + piecesPrice;
          costs.compare_at_price = costs.compare_at_price + piecesPrice;
          updated = true;
        }

        // Return costs
        return costs;
      },
      {
        price: 0,
        compare_at_price: 0,
      }
    );
  }

  /**
   * Step 3 - Calculate the total price
   * @param {object} matrixCosts - price from the matrix row
   * @param {object} surchargeCosts - additional costs from the surcharges
   * @returns {object} formatted price
   */
  totalPrice(matrixCosts, surchargeCosts) {
    const price = matrixCosts.price + surchargeCosts.price;
    const compareAtPrice = matrixCosts.compare_at_price + surchargeCosts.compare_at_price;

    // Price can't be higher then 9.999.999,99 because of token generation
    if (price > 999999999 || compareAtPrice > 999999999) {
      return {
        ...this.price.format(this.product.max_price, this.product.max_price),
        errors: [
          {
            type: 'general',
            handle: 'price_to_high',
            message: `The price is to high. Please contact us for a quote.`,
          },
        ],
      };
    }
    return this.price.format(price, compareAtPrice);
  }
}

export default PriceCalculation;
